package com.merkost.handpass.admin.components

import androidx.compose.runtime.Composable
import com.merkost.handpass.sharedweb.components.Icon
import com.merkost.handpass.sharedweb.components.WebIcon
import com.merkost.handpass.sharedweb.components.WebSegment
import com.merkost.handpass.sharedweb.components.WebSegmentedControl
import com.merkost.handpass.sharedweb.theme.AccentRing
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.OnSurface
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.RadiusControl
import com.merkost.handpass.sharedweb.theme.SurfaceMuted
import com.merkost.handpass.sharedweb.theme.ThemeMode
import com.merkost.handpass.sharedweb.theme.TouchTarget
import com.merkost.handpass.sharedweb.theme.transitionColors
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLButtonElement

private fun ThemeMode.icon(): WebIcon = when (this) {
    ThemeMode.LIGHT -> WebIcon.Sun
    ThemeMode.DARK -> WebIcon.Moon
    ThemeMode.SYSTEM -> WebIcon.Monitor
}

private fun ThemeMode.label(): String = name.lowercase().replaceFirstChar { it.uppercase() }

val AdminThemeCyclerButtonStyle = CssStyle {
    base {
        Modifier
            .width(TouchTarget.value())
            .height(TouchTarget.value())
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .padding(HandpassSpacing.SM)
            .borderRadius(RadiusControl.value())
            .backgroundColor(SurfaceMuted.value())
            .color(OnSurfaceMuted.value())
            .cursor(Cursor.Pointer)
            .transitionColors()
            .styleModifier {
                property("border", "none")
                property("font", "inherit")
            }
    }
    hover {
        Modifier.color(OnSurface.value())
    }
    focusVisible {
        Modifier
            .outline(0.px, LineStyle.None, Color("transparent"))
            .boxShadow(spreadRadius = 3.px, color = AccentRing.value())
    }
}

@Composable
fun AdminThemeControl(
    current: ThemeMode,
    compact: Boolean,
    onSelect: (ThemeMode) -> Unit,
) {
    if (compact) {
        Button(
            attrs = AdminThemeCyclerButtonStyle.toAttrs<AttrsScope<HTMLButtonElement>> {
                val next = when (current) {
                    ThemeMode.LIGHT -> ThemeMode.DARK
                    ThemeMode.DARK -> ThemeMode.SYSTEM
                    ThemeMode.SYSTEM -> ThemeMode.LIGHT
                }
                onClick { onSelect(next) }
                attr("type", "button")
                attr("aria-label", "Theme: ${current.label()}. Click to change")
                attr("title", "Theme: ${current.label()}")
            }
        ) {
            Icon(current.icon(), size = 16)
        }
    } else {
        Div({ style { property("margin-bottom", "${HandpassSpacing.XS}") } }) {
            WebSegmentedControl(
                label = "Theme",
                segments = listOf(
                    WebSegment(ThemeMode.LIGHT, "Light", WebIcon.Sun),
                    WebSegment(ThemeMode.DARK, "Dark", WebIcon.Moon),
                    WebSegment(ThemeMode.SYSTEM, "System", WebIcon.Monitor),
                ),
                selected = current,
                onSelect = onSelect,
            )
        }
    }
}
